"use client";

import { useRef, useState, type FormEvent } from "react";
import { MessageSquare, X } from "lucide-react";
import { Button } from "./ui/Button";
import type { RoomFeedback } from "@/lib/room-feedback";
import { roomHeaders } from "@/lib/room-client";

export function DesignFeedback({ designId, initialFeedback }: { designId: string; initialFeedback?: RoomFeedback }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [saved, setSaved] = useState(initialFeedback);
  const [rating, setRating] = useState<number | null>(initialFeedback?.rating ?? null);
  const [comment, setComment] = useState(initialFeedback?.comment ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const open = () => {
    setRating(saved?.rating ?? null);
    setComment(saved?.comment ?? "");
    setError("");
    dialog.current?.showModal();
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`/api/designs/${encodeURIComponent(designId)}/feedback`, {
        method: "POST", headers: roomHeaders(designId, { "Content-Type": "application/json" }), body: JSON.stringify({ rating, comment }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Could not save your feedback. Please try again.");
      setSaved(data.feedback);
      dialog.current?.close();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save your feedback. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <section className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-clay/20 bg-clay/5 p-5" aria-label="Design feedback">
      <div>
        <h3 className="text-lg font-semibold text-ink">How did your room turn out?</h3>
        <p className="mt-1 text-sm text-ink-soft" role="status">{saved ? "Thanks — your feedback is saved with this room." : "Help us improve the beta. Tell us what worked and what needs attention."}</p>
      </div>
      <Button variant="outline" onClick={open} className="w-full sm:w-auto shrink-0" aria-haspopup="dialog"><MessageSquare className="w-4 h-4" />{saved ? "Edit feedback" : "Give feedback"}</Button>
      <dialog ref={dialog} aria-labelledby="feedback-title" aria-describedby="feedback-description"
        onCancel={event => { if (saving) event.preventDefault(); }}
        className="m-auto w-[calc(100%-2rem)] max-w-md max-h-[90dvh] overflow-y-auto rounded-2xl border border-line bg-paper p-6 sm:p-8 text-left text-ink shadow-xl backdrop:bg-ink/60 backdrop:backdrop-blur-sm">
        <button type="button" aria-label="Close feedback" disabled={saving} onClick={() => dialog.current?.close()}
          className="absolute top-4 right-4 p-2 rounded-full bg-paper-deep text-ink-soft hover:text-ink disabled:opacity-50"><X className="w-5 h-5" /></button>
        <div className="w-12 h-12 bg-clay/10 rounded-full flex items-center justify-center mb-4 mx-auto">
          <MessageSquare className="w-6 h-6 text-clay" />
        </div>
        <h2 id="feedback-title" className="text-xl font-bold text-center">Your room feedback</h2>
        <p id="feedback-description" className="text-sm text-ink-soft mt-2 text-center">Something you love, something that looks wrong, or a problem along the way? Your feedback helps us improve.</p>
        <form onSubmit={submit} className="mt-6 space-y-5">
          <fieldset disabled={saving}>
            <legend className="text-sm font-semibold mb-3">How happy are you with the design?</legend>
            <div className="grid grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5].map(value => <label key={value} className={`flex min-w-0 flex-col items-center gap-2 rounded-xl border p-3 cursor-pointer focus-within:ring-2 focus-within:ring-clay/40 ${rating === value ? "border-clay bg-clay/10" : "border-line"}`}>
                <input type="radio" name="feedback-rating" className="accent-clay" aria-label={`${value} out of 5`} checked={rating === value} onChange={() => setRating(value)} />
                <span className="font-semibold">{value}</span>
              </label>)}
            </div>
            <div className="flex justify-between text-xs text-ink-soft mt-2"><span>Needs work</span><span>Love it</span></div>
            {rating !== null && <button type="button" onClick={() => setRating(null)} className="mt-2 text-xs text-ink-soft underline underline-offset-2">Clear rating</button>}
          </fieldset>
          <div>
            <label htmlFor="feedback-comment" className="block text-sm font-semibold mb-2">What should we know?</label>
            <textarea id="feedback-comment" value={comment} onChange={event => setComment(event.target.value)} maxLength={2000} rows={4} disabled={saving}
              aria-describedby="feedback-comment-hint" placeholder="The colours, furniture choices, layout, or anything that didn't work…" className="w-full rounded-xl border border-line bg-paper-deep p-3 text-sm focus:outline-none focus:ring-2 focus:ring-clay/40" />
            <p id="feedback-comment-hint" className="flex justify-between gap-2 text-xs text-ink-soft"><span>A rating, a comment, or both.</span><span>{comment.length}/2,000</span></p>
          </div>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <Button type="submit" disabled={saving || (rating === null && !comment.trim())} className="w-full">{saving ? "Saving feedback…" : "Save feedback"}</Button>
        </form>
      </dialog>
    </section>
  );
}
